'use client';

import { cn } from '@/lib/utils';
import { openFormModal } from '@/lib/utils/modal-event';
import { ArrowRight } from 'lucide-react';
import { HTMLAttributes } from 'react';
import { Button } from '../button/button';
import { Image } from '../image/image';
import styles from './link.module.scss';

export type LinkProps = {
  // Contentful fields (matching the JSON structure)
  label: string;
  url?: string;
  type: 'Button' | 'Link';
  buttonStyle?: 'Pink' | 'White';
  icon?: { src: string; alt?: string | null } | undefined;
  withArrow?: boolean;
  target?: '_blank' | '_self' | '_parent' | '_top';
  rel?: string;
  size?: 'default' | 'large';
  // Form destination support - all TypeForm fields
  destination?: 'URL' | 'Page' | 'Form';
  ctaId?: string;
  formId?: number;
  formSlugId?: string;
  badge?: string;
  title?: string;
  description?: string;
  successTitle?: string;
  successDescription?: string;
  successCTA?: {
    label?: string;
    url?: string;
    type?: 'Button' | 'Link';
  };
} & HTMLAttributes<HTMLElement>;

export const Link = ({
  label,
  url,
  type,
  buttonStyle,
  icon,
  withArrow = false,
  target,
  rel,
  size = 'default',
  className,
  destination,
  ctaId,
  formId,
  formSlugId,
  badge,
  title,
  description,
  successTitle,
  successDescription,
  successCTA,
  ...props
}: LinkProps) => {
  // Early return if no required fields
  if (!label) return null;
  // url is only required for non-Form destinations
  if (!url && destination !== 'Form') return null;

  // Icon data (already resolved from asset)
  const iconData = icon ?? { src: '', alt: '' };

  // Provide fallback href for Form destinations
  const finalUrl = url || '#';

  // Detect external URLs (starting with http:// or https://)
  const isExternalUrl = finalUrl.startsWith('http://') || finalUrl.startsWith('https://');
  const isAnchor = finalUrl.startsWith('#');

  // Default target: _blank for external URLs, _self for internal/anchor URLs
  // Only use default if target is not explicitly provided
  const finalTarget = target ?? (isExternalUrl && !isAnchor ? '_blank' : '_self');

  // Only set rel='noopener noreferrer' when target='_blank'
  const linkRel = finalTarget === '_blank' ? rel || 'noopener noreferrer' : rel;

  // Handle form destination clicks
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (destination === 'Form' && formId && formSlugId) {
      e.preventDefault(); // Stop default link navigation
      openFormModal({
        formId,
        slugId: formSlugId,
        ctaId,
        badge,
        title,
        description,
        successTitle,
        successDescription,
        successCTA,
      });
    }
    // Otherwise, allow normal link navigation
  };

  const iconElement = iconData.src ? (
    <Image
      src={iconData.src}
      alt={iconData.alt || label}
      className={styles.icon}
      width={16}
      height={16}
    />
  ) : null;

  if (type === 'Link') {
    return (
      <a
        href={finalUrl}
        target={finalTarget}
        rel={linkRel}
        onClick={handleClick}
        className={cn(
          styles.hyperlink,
          buttonStyle === 'White' ? styles['hyperlink--white'] : styles['hyperlink--pink'],
          className,
        )}
        {...props}
      >
        {iconElement}
        {label}
        {withArrow && <ArrowRight width={20} height={20} />}
      </a>
    );
  }

  return (
    <a
      href={finalUrl}
      target={finalTarget}
      rel={linkRel}
      onClick={handleClick}
      className={cn(
        styles.button,
        iconData.src &&
          (buttonStyle === 'White' ? styles['button--icon-dark'] : styles['button--icon-white']),
      )}
      {...props}
    >
      <Button
        className={cn(styles.button, className)}
        withArrow={withArrow}
        size={size}
        variant={buttonStyle === 'White' ? 'secondary' : 'primary'}
      >
        <span className={styles.button_label}>
          {iconElement}
          {label}
        </span>
      </Button>
    </a>
  );
};

Link.displayName = 'Link';
